import type { DynamicConvoStarterId } from "./selectDynamicConvoStarters";

import {
  getDynamicConvoStarterLabel,
  getDynamicConvoStarterMessage,
} from "./dynamicConvoStarterStrings";
import { useCareerScoutStartersRandomOrdering } from "./proctor";
import { useStarterChipMaxWidth } from "./useStarterChipMaxWidth";
import {
  logCareerScoutProductButtonTap,
  logCareerScoutProductImpression,
} from "../../../../../ace/logging/signals";
import { AILogo, Box, Pressable, ScrollView, Text } from "../../../../../mads";
import { useI18n } from "../../../../../shared/I18n";
import {
  encodeViewjobStarterSourceContext,
  getConversationForStarter,
  getConversationForViewjob,
  setActiveViewjobJobKey,
} from "../../../../../shared/lib/conversationStarter/ConversationStarterStorage";
import { JSTModalScreen } from "../../../../../shared/native-modules/tare/JSTModalScreen";
import { openModal } from "../../../../../shared/native-modules/tare/openModal";
import { useRunOnceAfterMount } from "../../../../../shared/util/useRunOnceAfterMount";
import { useViewjobOneGraphModel } from "../../../context/ViewJobDataContext";

import { borderRadius, colors, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useCallback, useMemo } from "react";
import { StyleSheet } from "react-native";

interface StarterChip {
  /** Stable id used to resolve translations via literal gettext calls. */
  id: DynamicConvoStarterId;
  /** Visible chip text in English. Kept as the analytics/impression key. */
  label: string;
  /** English text sent to Career Scout when the chip is tapped. Falls back to
   *  `label` when omitted. */
  message?: string;
}

export const CS_STARTER_CHIPS: StarterChip[] = [
  { id: "roleDetails", label: "What would I be doing?" },
  { id: "qualifications", label: "What experience do I need?" },
  {
    id: "differentQuestion",
    label: "I have a different question",
    message: "I have a different question about this job",
  },
];

const shuffleChips = (chips: StarterChip[]): StarterChip[] => {
  const result = [...chips];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
};

export interface AskCareerScoutStartersProps {
  horizontalBleedInset?: number;
}

export const AskCareerScoutStarters: React.FC<AskCareerScoutStartersProps> = ({
  horizontalBleedInset,
}) => {
  const gt = useI18n();
  const ogModel = useViewjobOneGraphModel();
  const jobKey = ogModel?.job.jobKey;
  const jobTitle = ogModel?.job.jobTitle;
  const companyName = ogModel?.employer?.companyName;
  const randomizeOrder = useCareerScoutStartersRandomOrdering();

  const bleedInset = horizontalBleedInset && horizontalBleedInset > 0 ? horizontalBleedInset : 0;
  const maxChipWidth = useStarterChipMaxWidth(bleedInset);

  // Decide the order once per mount so it doesn't reshuffle on every render.
  const chips = useMemo(
    () => (randomizeOrder ? shuffleChips(CS_STARTER_CHIPS) : CS_STARTER_CHIPS),
    [randomizeOrder],
  );

  const handlePress = useCallback(
    (label: string, message: string) => {
      // All chips share a single conversation per jobKey: open the existing
      // thread if any, otherwise create a new one.
      const existingConversationId = jobKey ? getConversationForViewjob(jobKey) : null;

      if (jobKey) {
        setActiveViewjobJobKey(jobKey);
      }

      // Don't re-send a starter message that's already in the thread's
      // history. `getConversationForStarter` doubles as a "this starter
      // has been used in this job's conversation" marker.
      const starterAlreadySent = jobKey
        ? getConversationForStarter(message, jobKey) !== null
        : false;
      const starterMessage = starterAlreadySent ? null : message;

      const sourceContext = jobKey
        ? encodeViewjobStarterSourceContext(jobKey, message, {
            jobTitle,
            companyName,
          })
        : "viewjob";

      logCareerScoutProductButtonTap({
        elementName: "askCareerScout",
        assistantMessageId: null,
        from: "viewJob",
        jobKey: jobKey ?? undefined,
        suggestion: label,
      });

      void openModal(
        JSTModalScreen.aceConversation({
          starterMessage,
          conversationId: existingConversationId,
          sourceContext,
        }),
        "viewjob",
      );
    },
    [jobKey, jobTitle, companyName],
  );

  useRunOnceAfterMount(() => {
    logCareerScoutProductImpression({
      elementName: "askCareerScout",
      assistantMessageId: null,
      from: "viewJob",
      jobKey: jobKey ?? undefined,
      suggestions: randomizeOrder ? chips.map((chip) => chip.label).join(", ") : undefined,
    });
  });

  const renderChip = (chip: StarterChip) => {
    const label = getDynamicConvoStarterLabel(gt, chip.id);
    const message = getDynamicConvoStarterMessage(gt, chip.id);
    return (
      <Pressable
        key={chip.id}
        onPress={() => handlePress(label, message)}
        sx={({ pressed }) => [
          styles.chip,
          { maxWidth: maxChipWidth },
          pressed && styles.chipPressed,
        ]}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <Box sx={styles.chipContent}>
          <AILogo size="sm" />
          <Text level={2} sx={styles.chipText} numberOfLines={1} selectable={false}>
            {label}
          </Text>
        </Box>
      </Pressable>
    );
  };

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      testID="ask-career-scout-starters-scroll"
      sx={bleedInset ? { marginStart: -bleedInset, marginEnd: -bleedInset } : undefined}
      contentContainerSx={[
        styles.scrollContent,
        bleedInset ? { paddingStart: bleedInset } : undefined,
      ]}
    >
      {chips.map(renderChip)}
    </ScrollView>
  );
};

export const CS_STARTER_QUESTIONS = CS_STARTER_CHIPS.map((chip) => chip.message ?? chip.label);

const CHIP_TAIL_RADIUS = 6;

const styles = StyleSheet.create({
  scrollContent: {
    flexDirection: "row",
    gap: spacing.s2,
  },
  chip: {
    borderWidth: 1,
    borderColor: colors.borderWeak,
    borderTopLeftRadius: borderRadius.lg,
    borderTopRightRadius: borderRadius.lg,
    borderBottomLeftRadius: borderRadius.lg,
    borderBottomRightRadius: CHIP_TAIL_RADIUS,
    paddingHorizontal: spacing.s4,
    paddingVertical: spacing.s2,
    minHeight: 42,
    backgroundColor: colors.background,
    justifyContent: "center",
  },
  chipPressed: {
    backgroundColor: colors.backgroundActionActive,
    borderColor: colors.borderActionActive,
  },
  chipContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.s2,
  },
  chipText: {
    color: colors.foreground,
  },
});
